import React, { useEffect, useState } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import WalletService from "../../services/WalletService";
import "./AdminModalForm.css";

// Admin modal for editing wallet data (balance, currency, owner links)
const AdminEditWalletModal = ({ isOpen, onClose, wallet, onUpdated }) => {
  // Local editable fields for wallet
  const [formData, setFormData] = useState({
    id: "",
    balance: "",
    currency: "",
    user_id: "",
    carrier_id: "",
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Initialize form when modal opens or wallet changes
  useEffect(() => {
    if (isOpen && wallet) {
      setFormData({
        id: wallet.id || "",
        balance: wallet.balance === null || wallet.balance === undefined ? "" : wallet.balance,
        currency: wallet.currency || "",
        user_id: wallet.user_id || "",
        carrier_id: wallet.carrier_id || "",
      });
      setError("");
      setSuccess("");
    }
  }, [isOpen, wallet]);

  // Do not render if closed or missing wallet
  if (!isOpen || !wallet) return null;

  // Handle input change with numeric conversion
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
    setError("");
  };

  // Build payload for update API
  const buildPayload = () => ({
    id: formData.id,
    balance: Number(formData.balance),
    currency: formData.currency,
    user_id: formData.user_id || null,
    carrier_id: formData.carrier_id || null,
  });

  // Handle form submit and call updateWallet
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      const payload = buildPayload();

      await WalletService.updateWallet(payload);

      setSuccess("Wallet updated successfully.");
    } catch (err) {
      setError(err.message || "Failed to update wallet");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={`Edit Wallet: ${formData.id}`}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MainButton>
        </>
      }
    >
      {/* Wallet edit form */}
      <form onSubmit={handleSubmit} className="admin-modal-form">
        {/* Read-only wallet ID */}
        <label>
          Wallet ID
          <MainInput type="text" name="id" value={formData.id} disabled />
        </label>

        {/* Balance field */}
        <label>
          Balance *
          <MainInput
            type="number"
            name="balance"
            value={formData.balance}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Currency field */}
        <label>
          Currency *
          <MainInput
            type="text"
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* User id / email field */}
        <label>
          User ID / Email
          <MainInput type="text" name="user_id" value={formData.user_id} onChange={handleChange} disabled={saving} />
        </label>

        {/* Carrier id field */}
        <label>
          Carrier ID
          <MainInput
            type="text"
            name="carrier_id"
            value={formData.carrier_id}
            onChange={handleChange}
            disabled={saving}
          />
        </label>

        {/* Error feedback */}
        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Success feedback */}
        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminEditWalletModal;
